import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {CommonModule, NgOptimizedImage} from '@angular/common';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ProgressService} from '../../../shared/services/progress.service';
import {SnackbarService} from '../../../shared/services/snackbar.service';
import {DetailHeaderComponent} from "../../../shared/components/config-detail/detail-header/detail-header.component";
import {ConfigurationService} from "../../../shared/services/configuration.service";
import {
    ConfigDetailSkeletonComponent
} from "../../../shared/components/config-detail-skeleton/config-detail-skeleton.component";
import {JsonataExpressionsDataService} from "../services/jsonata-expressions-data.service";
import {JsonataExpressionsService} from "../services/jsonata-expressions-service.model";
import {ConfigDetailComponent} from "../../../shared/components/config-detail/config-detail.component";
import {ConfigRouteEnum} from "../../../shared/enums/config-route.enum";
import {DetailBodyComponent} from "../../../shared/components/config-detail/detail-body/detail-body.component";
import {debounceTime, distinctUntilChanged} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {RefreshService} from "../../../shared/services/refresh.service";
import {CardModule} from "primeng/card";
import {InputTextModule} from "primeng/inputtext";
import {IconField} from "primeng/iconfield";
import {InputIcon} from "primeng/inputicon";
import {ButtonModule} from "primeng/button";
import {TableModule} from "primeng/table";

@Component({
    selector: 'app-jsonata-expressions-overview',
    templateUrl: './jsonata-expressions-overview.component.html',
    styleUrls: ['./jsonata-expressions-overview.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        FormsModule,
        DetailHeaderComponent,
        NgOptimizedImage,
        ConfigDetailSkeletonComponent,
        ConfigDetailComponent,
        DetailBodyComponent,
        CardModule,
        InputTextModule,
        IconField,
        InputIcon,
        ButtonModule,
        TableModule,
    ]
})
export class JsonataExpressionsOverviewComponent implements OnInit {
    protected readonly progressService = inject(ProgressService);
    private readonly snackBar = inject(SnackbarService);
    private readonly jsonataExpressionsDataService = inject(JsonataExpressionsDataService);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly config = inject(ConfigurationService).configuration;
    private readonly destroyRef = inject(DestroyRef);
    private readonly refreshService = inject(RefreshService);
    private readonly activatedRoute = inject(ActivatedRoute);

    protected readonly ConfigRouteEnum = ConfigRouteEnum;

    readonly searchId = "JSONataExpressions";
    dataSource: JsonataExpressionsService[] = [];
    searchInput = new FormControl('');
    logoUrl: string | null = this.config.app.defaultLogo;

    private initialLoadDone = false;

    ngOnInit() {
        this.setupSearch();
        this.loadJsonataTable();

        this.refreshService.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.searchInput.setValue('');
            this.initialLoadDone = false;
            this.loadJsonataTable();
        });
    }

    private setupSearch(): void {
        this.searchInput.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(searchTerm => {
            this.loadJsonataTable((searchTerm ?? '').trim().toLowerCase());
        });
    }

    loadJsonataTable(searchTerm: string = ''): void {
        const showSkeleton = !this.initialLoadDone;
        if (showSkeleton) {
            this.progressService.startLoading();
        }

        const applicationSettings = this.jsonataExpressionsDataService.getJsonata();

        applicationSettings.subscribe({
            next: (data: JsonataExpressionsService[]) => {
                if (searchTerm) {
                    this.dataSource = data.filter(s =>
                        s.identification.toLowerCase().includes(searchTerm) ||
                        s.tenantName.toLowerCase().includes(searchTerm) ||
                        s.id?.toString().includes(searchTerm) ||
                        (s.active ? 'active' : 'inactive').includes(searchTerm)
                    );
                } else {
                    this.dataSource = data;
                }
                if (showSkeleton) {
                    this.progressService.stopLoading();
                    this.initialLoadDone = true;
                }
            },
            error: () => {
                this.snackBar.Error(this.translate.instant("snackbar.jsonata.error"));
                this.progressService.stopLoading();
            }
        });
    }

    protected addJsonataExpression() {
        this.router.navigate([ConfigRouteEnum.JSONATA, 'new']);
    }

    jsonataDetail(row: JsonataExpressionsService) {
        this.router.navigate([row.id], {relativeTo: this.activatedRoute});
    }
}
